"use client"

import { useEffect, useState } from "react"

interface SaleGateProps {
  /**
   * Sale window start (ISO-8601). Before this instant the gate shows `fallback`
   * (list price); at/after it (and before `endsAt`) it shows `children`.
   */
  startsAt: string
  /** Sale window end (ISO-8601). At/after this the gate shows `fallback` again. */
  endsAt: string
  /**
   * Whether the sale is active at SSR/cache-fill time (server-resolved via
   * `activeSaleForSku`). The gate initializes to this so the server HTML and the
   * first client paint agree (no hydration mismatch); an effect then reconciles
   * against the live client clock.
   */
  initiallyActive: boolean
  /** Discounted content — rendered only while `startsAt <= now < endsAt`. */
  children: React.ReactNode
  /** List-price content — rendered before the window opens and after it closes. */
  fallback: React.ReactNode
}

/**
 * Client gate that shows the discounted display only within the live sale
 * window, flipping at BOTH edges.
 *
 * The pages that render a sale are cached (`'use cache'`), so their server HTML
 * is a snapshot from cache-fill while `/api/checkout` and the merchant feed
 * track the live clock. The cache lifetime is bound to the sale boundary so new
 * requests are fresh, but an ALREADY-OPEN tab keeps its delivered HTML across a
 * transition — so without this gate a buyer sitting on the page would see the
 * stale price (list before `startsAt`, sale after `endsAt`) while checkout
 * charges the live amount. This re-evaluates the window on mount (the served
 * HTML may be stale) and each second after, so the visible price flips to the
 * sale the instant `startsAt` passes and back to list the instant `endsAt` does
 * — keeping the page in step with what checkout charges at both edges. Initial
 * state is the server's `initiallyActive` so SSR and first client paint agree
 * (no hydration mismatch); the reconciliation happens in an effect.
 *
 * It is rendered whenever a sale is CONFIGURED (not only while active), so the
 * `startsAt` edge has a timer too; for a SKU with no configured sale the caller
 * renders the list price directly and never mounts this.
 */
export function SaleGate({
  startsAt,
  endsAt,
  initiallyActive,
  children,
  fallback,
}: SaleGateProps): JSX.Element {
  const [active, setActive] = useState(initiallyActive)

  useEffect(() => {
    const start = new Date(startsAt).getTime()
    const end = new Date(endsAt).getTime()
    if (Number.isNaN(start) || Number.isNaN(end)) return
    const compute = (): boolean => {
      const now = Date.now()
      return now >= start && now < end
    }
    // The served HTML can be stale (cached up to its boundary); reconcile to the
    // live clock immediately on mount, then keep it in step every second.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reconcile a possibly-stale SSR snapshot with the live client clock
    setActive(compute())
    const id = window.setInterval(() => setActive(compute()), 1000)
    return () => window.clearInterval(id)
  }, [startsAt, endsAt])

  return <>{active ? children : fallback}</>
}
